import { computed, makeObservable } from 'mobx'

import { Collection } from 'modules/core/lib/mobx-rest'
import User, { IUser } from 'modules/core/store/models/User'

// FIXME: Core must not be coupled with other modules.
import type Employee from 'modules/employees/store/models/Employee'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'

export class Users extends Collection<User> {
  constructor(data = []) {
    super(data)
    makeObservable(this, {
      current: computed,
      active: computed,
      terminated: computed,
      notTerminated: computed,
    })
  }

  get cacheKey(): string {
    return 'users'
  }

  get indexes() {
    return ['current', 'email']
  }

  /**
   * @deprecated Before using this method, consider using the `useCurrentSession` hook since
   * we are trying to move away from using mobx-rest and use GraphQL instead.
   */
  get current(): User {
    const user = this.find({
      current: true,
    })

    if (!user) {
      throw Error('Error finding users with current = true')
    }

    return user
  }

  get active(): User[] {
    return this.models.filter((user) => user.isActive)
  }

  get terminated(): User[] {
    return this.models.filter((user) => user.isTerminated)
  }

  get notTerminated(): User[] {
    return this.models.filter((user) => !user.isTerminated)
  }

  get notTerminatedExcludingExternals(): User[] {
    return this.models.filter((user) => !user.isTerminated && !!user.employee)
  }

  async findOrCreate(attributes: Partial<IUser>): Promise<IUser> {
    const existingUser = this.find({ email: attributes.email })

    if (existingUser) {
      return existingUser.toJS()
    }

    return await this.create(attributes)
  }

  model() {
    return User
  }
}

const singleton: Users = new Users()

export const employeeUser = (employee: Employee) => {
  const userId = employee.attributes.access_id
  const user = singleton.get(userId)

  if (!user) {
    throw Error(`Error getting users with id = ${userId}`)
  }

  return user
}

export const isEmployeeTimeoffManagedByCurrent = (employee: Employee) => {
  if (!singleton.current.employee) return false

  return singleton.current.employee.attributes.id === employee.attributes.timeoff_manager_id
}

export const questionGroupRespondent = (questionGroup: QuestionGroup) => {
  return singleton.get(questionGroup.attributes.respondent_id)
}
export default singleton
